import { ReactNode, ComponentType } from 'react';

import {
  BackrsIcons,
  AskLubavButtonEndpoint,
  ContentNugget,
  NUGGET_THEMES,
  NuggetVariant,
  ActivityNugget,
  NuggetUXVariant,
  PanelNugget,
  Tier,
  BackrsUser,
} from '@backrs_ui/common-ui';

import { AskLubavErrorCode } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavError';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';
import {
  deleteContentNuggetBookmark,
  postContentNuggetBookmark,
} from '@backrs_ui/grow-ui/src/redux/user/thunks/nuggetBookmark';
import { ENVIRONMENT } from '@backrs_ui/grow-ui/src/settings';

import { PostHog } from 'posthog-js';

import { handleCareerPathApiCall } from './askLubavUtils/askLubav.careerPath.util';
import { handleInternshipApiCall } from './askLubavUtils/askLubav.internship.util';

const PROSPER_URL = process.env.REACT_APP_GROW_URL;
const BACKRS_URL = process.env.REACT_APP_BACKRS_URL;
const GROW_URL = process.env.REACT_APP_GROW_URL;
const IS_PROD = ENVIRONMENT === 'production';

type NuggetMapperParams = Readonly<{
  nugget: ContentNugget;
  user?: BackrsUser;
  mostRecentResponseSetTrack?: Tier;
  askLubavResponseAccumulator?: (chunk: string) => void;
  askLubavButtonResponse?: string;
  setAskLubavButtonResponse?: (response: string) => void;
  AskLubavErrorDisplay?: ComponentType<{
    errorCode: AskLubavErrorCode | undefined;
  }>;
}>;

export function nuggetMapper({
  nugget,
  user,
  mostRecentResponseSetTrack,
  askLubavResponseAccumulator,
  askLubavButtonResponse,
  setAskLubavButtonResponse,
  AskLubavErrorDisplay,
}: NuggetMapperParams): NuggetUXVariant {
  const enableAllLubavOptions = !IS_PROD || user?.roles?.some((role) => role.name === 'Lubav Beta');

  const defaultTextNugget: NuggetUXVariant = {
    type: 'resource',
    title: nugget.title,
    url: nugget.external_url || undefined,
    previewImage: nugget.preview_media?.media_url || undefined,
  };

  switch (nugget.variant) {
    case 'opportunity':
      return {
        type: 'opportunity',
        deadline: nugget.end_date || '',
        mediaUrl: nugget.media?.media_url || '',
      };

    case 'video-external':
      return nugget.external_url
        ? {
            type: 'video-external',
            url: nugget.external_url,
            previewImage: nugget.preview_media?.media_url,
          }
        : defaultTextNugget;

    case 'video-internal':
      return nugget.media?.media_url
        ? {
            type: 'video-internal',
            url: nugget.media.media_url,
            previewImage: nugget.preview_media?.media_url,
          }
        : defaultTextNugget;

    case 'article':
      return {
        type: 'article',
        articleUrl: nugget.external_url,
        title: nugget.title,
        previewImage: nugget.preview_media?.media_url,
      };

    case 'document':
      return nugget.media?.media_url
        ? {
            type: 'document',
            title: nugget.title,
            internalMediaItemUrl: nugget.media.media_url,
            externalUrl: nugget.external_url,
          }
        : defaultTextNugget;

    case 'audio':
      return nugget.external_url
        ? {
            type: 'audio',
            url: nugget.external_url,
            title: nugget.title,
          }
        : defaultTextNugget;

    case 'institution':
      if (nugget.external_url) {
        return {
          type: 'institution',
          url: nugget.external_url,
          previewImage: nugget.preview_media?.media_url,
        };
      }

      return defaultTextNugget;

    case 'book':
      return {
        ...defaultTextNugget,
        type: 'book',
      };

    case 'competency':
      return {
        type: 'competency',
        slug: nugget.slug,
      };

    case 'event':
      return {
        type: 'event',
        date: nugget.start_date || '',
        previewImage: nugget.preview_media?.media_url,
      };

    case 'profile-external':
      return {
        type: 'profile-external',
        abbreviatedName: nugget.external_profile_info?.first_name
          ? `${nugget.external_profile_info?.first_name} ${nugget.external_profile_info?.last_name}`
          : nugget.title,
        linkToProfile: nugget.external_profile_info?.url || nugget.external_url,
        profileImageUrl:
          nugget.external_profile_info?.profile_image_url || nugget.preview_media?.media_url,
        location: `${nugget.external_profile_info?.city}, ${nugget.external_profile_info?.state}, ${nugget.external_profile_info?.country}`,
        jobTitle: nugget.external_profile_info?.job_title,
        employer: nugget.external_profile_info?.employer,
        school: nugget.external_profile_info?.school,
      };

    case 'profile-internal':
      return {
        type: 'profile-internal',
        abbreviatedName: `${nugget.profile_info?.first_name} ${nugget.profile_info?.last_name}`,
        location: nugget.profile_info?.location,
        jobTitle: nugget.profile_info?.job_title,
        role: nugget.profile_info?.role,
        employer: nugget.profile_info?.employer,
        school: nugget.profile_info?.school,
        profileImageUrl: nugget.profile_info?.profile_image || '/default.svg',
        profileCoverImageUrl: nugget.profile_info?.profile_cover_image,
        linkToProfile: `${GROW_URL}/profile/${nugget.profile_info?.id}`,
      };

    case 'scholarships':
      if (!nugget.scholarship_info) {
        return defaultTextNugget;
      }

      return {
        type: 'scholarships',
        scholarshipInfo: nugget.scholarship_info,
      };

    case 'internship':
      if (!nugget.internship_info) {
        return defaultTextNugget;
      }

      return {
        type: 'internship',
        internshipInfo: nugget.internship_info,
        askLubavButtonGroupProps: {
          isDialogOpen: true,
          tier: mostRecentResponseSetTrack,
          enableAllOptions: enableAllLubavOptions,
          user: user,
          handleApiCall: (url_key: AskLubavButtonEndpoint) =>
            handleInternshipApiCall({
              url_key,
              userId: user?.id,
              internship: nugget.internship_info || null,
              askLubavResponseAccumulator,
              setAskLubavButtonResponse,
            }),
          AskLubavErrorDisplay,
        },
        askLubavButtonResponse,
      };

    case 'post':
      return {
        type: 'post',
        text: nugget.description || '',
        headshotImg: nugget.post_info?.author?.profile_image || undefined,
        userAbbreviatedName: nugget.post_info?.author?.first_name
          ? `${nugget.post_info?.author.first_name} ${nugget.post_info?.author.last_name.charAt(0)}`
          : '',
        linkToBackrsProfile: nugget.post_info?.author?.id
          ? `${GROW_URL}/profile/${nugget.post_info?.author?.id}`
          : undefined,
        postDate: nugget.post_info?.created_at || '',
        postUrl: nugget.post_info?.id
          ? `${BACKRS_URL}/posts/${nugget.post_info?.id}/comments`
          : undefined,
      };

    case 'text-response':
      if (nugget.type === 'activity') {
        return defaultTextNugget;
      }

      return {
        type: 'text-response',
        text: nugget.description || '',
      };

    case 'career-path':
      return {
        type: 'career-path',
        title: nugget.title,
        careerPathInfo: nugget.career_path_info || null,
        isDialogOpen: true,
        tier: mostRecentResponseSetTrack,
        askLubavButtonGroupProps: nugget.career_path_info
          ? {
              isDialogOpen: true,
              tier: mostRecentResponseSetTrack,
              enableAllOptions: enableAllLubavOptions,
              user: user,
              handleApiCall: (url_key: AskLubavButtonEndpoint) =>
                handleCareerPathApiCall({
                  url_key,
                  currentDialogCareerPath: nugget.career_path_info || null,
                  userId: user?.id,
                  tier: mostRecentResponseSetTrack || null,
                  askLubavResponseAccumulator,
                  setAskLubavButtonResponse,
                }),
              AskLubavErrorDisplay,
            }
          : undefined,
        askLubavButtonResponse,
      };

    case 'generated-content':
      return {
        type: 'generated-content',
        generatedContentInfo: nugget.generated_content_info,
      };

    case 'employer':
      return {
        type: 'employer',
        employerInfo: nugget.employer_info,
      };

    default:
      return defaultTextNugget;
  }
}

export function determineTitleOnClick(nugget: ContentNugget): (() => void) | undefined {
  if (nugget.variant === 'scholarships') {
    const { scholarship_info } = nugget;

    if (!scholarship_info) {
      return undefined;
    }

    return () => window.open(scholarship_info.program_url, '_blank');
  }

  return undefined;
}

export function determineSubtitle(nugget: ContentNugget): string | undefined {
  if (nugget.variant === 'scholarships') {
    return nugget.scholarship_info?.donor.split(':')[0];
  }

  if (nugget.variant === 'internship') {
    return nugget.internship_info?.company;
  }

  return undefined;
}

export function determineTitle(nugget: ContentNugget): string {
  if (nugget.variant === 'internship') {
    return `${nugget.title}`;
  }

  return nugget.title || '';
}

export function determineDescription(nugget: ContentNugget): string {
  if (nugget.type === 'activity') {
    // activities can be text responses, but we handle displaying them on their own
    return nugget.description;
  }

  const listOfNoCustomDescriptionVariants: NuggetVariant[] = [
    'text-response',
    'scholarships',
    'internship',
    'post',
    'profile-external',
  ];

  if (listOfNoCustomDescriptionVariants.includes(nugget.variant)) {
    return '';
  }

  return nugget.description;
}

export function determineCustomMenuItems(
  nugget: ContentNugget,
): { icon: BackrsIcons; text: string; onClick: () => void }[] | undefined {
  if (['scholarships', 'internship'].includes(nugget.variant)) {
    const scholarshipSubmissionLink =
      nugget.scholarship_info?.submission_link || nugget.scholarship_info?.program_url;

    const internshipSubmissionLink =
      nugget.internship_info?.application_url || nugget.internship_info?.company_website;

    if (!scholarshipSubmissionLink && !internshipSubmissionLink) {
      return undefined;
    }

    return [
      {
        icon: 'IconApply',
        text: 'Apply',
        onClick: () => {
          window.open(scholarshipSubmissionLink || internshipSubmissionLink, '_blank');
        },
      },
    ];
  }

  return undefined;
}

export const handlePostHogCapture = (nugget: ContentNugget, postHog: PostHog) => {
  /** get type and string identifier (slug) for Posthog */
  const getSlugAndType = (
    type: NuggetVariant,
  ): {
    type: 'nugget' | 'scholarship' | 'profile' | 'career-path' | 'internship' | 'generated-content';
    slug: string;
  } => {
    switch (type) {
      case 'scholarships':
        return { type: 'scholarship', slug: nugget.scholarship_info?.name || '' };

      case 'internship':
        return { type: 'internship', slug: nugget.internship_info?.company || '' };

      case 'profile-internal':
        return {
          type: 'profile',
          slug: `${nugget.profile_info?.first_name} ${nugget.profile_info?.last_name}`,
        };

      case 'profile-external':
        return {
          type: 'profile',
          slug: `${nugget.external_profile_info?.first_name} ${nugget.external_profile_info?.last_name}`,
        };

      case 'career-path':
        return { type: 'career-path', slug: nugget.career_path_info?.name || '' };

      case 'generated-content':
        return { type: 'generated-content', slug: nugget.generated_content_info?.uuid || '' };

      default:
        return { type: 'nugget', slug: nugget.slug };
    }
  };

  const { slug, type } = getSlugAndType(nugget.variant);

  postHog.capture('shared_content_nugget', {
    slug,
    id: nugget.id,
    type,
  });
};

type GetShareUrlParams = Readonly<{
  contentNugget: ContentNugget;
}>;

export const getShareUrl = ({ contentNugget }: GetShareUrlParams): string => {
  switch (contentNugget.variant) {
    case 'scholarships':
      return `${PROSPER_URL}?scholarship=${contentNugget.scholarship_info?.id}`;

    case 'internship':
      return `${PROSPER_URL}?internship=${contentNugget.internship_info?.id}`;

    case 'profile-internal':
      return `${PROSPER_URL}?profile-in=${contentNugget.profile_info?.id}`;

    case 'profile-external':
      return `${PROSPER_URL}?profile-ex=${contentNugget.external_profile_info?.id}`;

    case 'career-path':
      return `${PROSPER_URL}?career-path=${contentNugget.career_path_info?.id}`;

    case 'post':
      return `${PROSPER_URL}?post=${contentNugget.post_info?.id}`;

    case 'generated-content':
      return `${PROSPER_URL}?generated-content=${contentNugget.generated_content_info?.uuid}`;

    default:
      return `${PROSPER_URL}?nugget=${contentNugget.slug}`;
  }
};

export function determinePanelNuggetRender({
  nugget,
  completedNuggetIds,
  onClick,
  key,
}: {
  nugget: ContentNugget;
  completedNuggetIds?: number[];
  onClick: (nugget: ContentNugget) => void;
  key: string;
}): ReactNode {
  switch (true) {
    case nugget.type === 'activity':
      return (
        <ActivityNugget
          title={nugget.title}
          hasUserCompleted={completedNuggetIds?.includes(nugget.id) || false}
          onClick={() => onClick(nugget)}
          key={key}
        />
      );
    case nugget.variant === 'scholarships':
      return (
        <PanelNugget
          title={nugget.title}
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
        />
      );

    case nugget.variant === 'internship':
      return (
        <PanelNugget
          title={nugget.title}
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
        />
      );
    case nugget.variant === 'generated-content':
      return (
        <PanelNugget
          title={nugget.title}
          body=""
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
        />
      );
    default:
      return (
        <PanelNugget
          title={nugget.title}
          body={nugget.description}
          variant={nuggetMapper({ nugget })}
          onClick={() => onClick(nugget)}
          key={key}
        />
      );
  }
}

export function groupNuggetsByCategory(bookmarksAsNuggets: ContentNugget[]) {
  const categorizedNuggets = bookmarksAsNuggets.reduce(
    (acc, nugget) => {
      if (nugget.variant === 'scholarships') {
        return {
          ...acc,
          scholarshipNuggets: [...acc.scholarshipNuggets, nugget],
        };
      }

      if (nugget.variant === 'internship') {
        return {
          ...acc,
          internshipNuggets: [...acc.internshipNuggets, nugget],
        };
      }

      if (nugget.variant === 'profile-internal') {
        return {
          ...acc,
          profileNuggets: [...acc.profileNuggets, nugget],
        };
      }

      if (nugget.variant === 'profile-external') {
        return {
          ...acc,
          externalProfileNuggets: [...acc.externalProfileNuggets, nugget],
        };
      }

      if (nugget.variant === 'career-path') {
        return {
          ...acc,
          careerPathNuggets: [...acc.careerPathNuggets, nugget],
        };
      }

      if (nugget.variant === 'post') {
        return {
          ...acc,
          postNuggets: [...acc.postNuggets, nugget],
        };
      }

      if (nugget.variant === 'institution' && nugget.college_info) {
        return {
          ...acc,
          collegeNuggets: [...acc.collegeNuggets, nugget],
        };
      }

      if (nugget.variant === 'generated-content') {
        return {
          ...acc,
          generatedContentNuggets: [...acc.generatedContentNuggets, nugget],
        };
      }

      switch (nugget.content_nugget_theme) {
        case NUGGET_THEMES.LEARN_AND_EXPLORE:
          return {
            ...acc,
            learnAndExploreNuggets: [...acc.learnAndExploreNuggets, nugget],
          };
        case NUGGET_THEMES.REAL_PEOPLE_REAL_STORIES:
          return {
            ...acc,
            realPeopleRealStoriesNuggets: [...acc.realPeopleRealStoriesNuggets, nugget],
          };
        case NUGGET_THEMES.GROW_BY_DOING:
          return {
            ...acc,
            growByDoingNuggets: [...acc.growByDoingNuggets, nugget],
          };
        default:
          return acc;
      }
    },
    {
      learnAndExploreNuggets: [] as ContentNugget[],
      realPeopleRealStoriesNuggets: [] as ContentNugget[],
      growByDoingNuggets: [] as ContentNugget[],
      scholarshipNuggets: [] as ContentNugget[],
      internshipNuggets: [] as ContentNugget[],
      profileNuggets: [] as ContentNugget[],
      externalProfileNuggets: [] as ContentNugget[],
      careerPathNuggets: [] as ContentNugget[],
      postNuggets: [] as ContentNugget[],
      collegeNuggets: [] as ContentNugget[],
      generatedContentNuggets: [] as ContentNugget[],
    },
  );

  return categorizedNuggets;
}

export const handleBookmark = ({
  bookmarkId,
  dispatch,
  nuggetId,
  variant,
}: {
  bookmarkId?: number;
  dispatch: AppDispatch;
  nuggetId: number;
  variant: ContentNugget['variant'];
}) => {
  if (bookmarkId) {
    // we just delete by the bookmark entry id, and not via the nugget/scholarship id
    dispatch(deleteContentNuggetBookmark(bookmarkId));

    return;
  }

  switch (variant) {
    case 'post':
      dispatch(postContentNuggetBookmark({ type: 'post', itemId: nuggetId }));

      return;
    case 'scholarships':
      dispatch(postContentNuggetBookmark({ type: 'scholarship', itemId: nuggetId }));

      return;

    case 'internship':
      dispatch(postContentNuggetBookmark({ type: 'internship', itemId: nuggetId }));

      return;

    case 'profile-internal':
      dispatch(postContentNuggetBookmark({ type: 'profile', itemId: nuggetId }));

      return;

    case 'profile-external':
      dispatch(postContentNuggetBookmark({ type: 'external-profile', itemId: nuggetId }));

      return;

    case 'career-path':
      dispatch(postContentNuggetBookmark({ type: 'career-path', itemId: nuggetId }));

      return;

    case 'generated-content':
      dispatch(postContentNuggetBookmark({ type: 'generated-content', itemId: nuggetId }));

      return;

    default:
      dispatch(postContentNuggetBookmark({ type: 'nugget', itemId: nuggetId }));
  }
};
